import React, { useRef, useState, useEffect } from 'react';

import { CssBaseline } from '@mui/material';
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider,
} from '@mui/material/styles';
import {
    breakpoints,
    componentsOverride,
    customShadows,
    palette,
    shadows,
    shape,
    typography,
} from '@sunwisesoftware/sunwise-ui';
import { APIProvider } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { NotificationsListener } from 'common/components';
import LoadingProcessView from 'common/modules/loadingView/ProcessView';
import { LoadingProvider } from 'common/utils/contexts';
import 'react-toastify/dist/ReactToastify.css';

import ToastAskPermission from './common/components/ToastAskPermission';
import main from './modules/main';
import * as profileActions from './modules/profile/actions';
import * as profileSelectors from './modules/profile/selectors';
import {
    components as themeDarkComponents,
    palette as themeDarkPalette,
} from './theme-dark';

const App = ({ history, selectedTheme = 'light', setTheme }) => {
    const loadingProcessRef = useRef(null);
    const [providerValue, setProviderValue] = useState({});

    const themeOptions = {
        breakpoints,
        customShadows,
        palette: selectedTheme === 'light' ? palette : themeDarkPalette,
        shadows,
        shape,
        typography,
    };
    const theme = createTheme(themeOptions);
    theme.components = componentsOverride(theme);
    if (selectedTheme === 'dark')
        theme.components = { ...themeDarkComponents(theme).components };

    useEffect(() => {
        if (loadingProcessRef.current !== null) {
            const { openLoading, closeLoading } = loadingProcessRef.current;
            setProviderValue({ openLoading, closeLoading });
        }
    }, [loadingProcessRef]);

    useEffect(() => {
        const storageTheme = localStorage.getItem('theme') || 'light';
        setTheme(storageTheme);
    }, []);

    return (
        <>
            <LoadingProcessView ref={loadingProcessRef} />
            <Router history={history}>
                <LoadingProvider value={providerValue}>
                    <APIProvider
                        apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY}
                        libraries={['drawing', 'geometry', 'marker']}
                    >
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme}>
                                <CssBaseline />
                                <NotificationsListener />
                                <ToastAskPermission />
                                <main.Container />
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </APIProvider>
                </LoadingProvider>
                <ToastContainer />
            </Router>
        </>
    );
};

App.propTypes = {
    history: PropTypes.object,
    selectedTheme: PropTypes.string,
    setTheme: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    selectedTheme: profileSelectors.getTheme,
});

const mapDispatchToProps = (dispatch) => ({
    setTheme: (value) => dispatch(profileActions.setTheme(value)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
)(App);
